import { inject, Injector } from '@angular/core';
import { StorageService } from '@techniek-team/storage';
import {
  EventTypes,
  OidcSecurityService,
  OpenIdConfiguration,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { format } from 'date-fns';
import {
  exhaustMap,
  filter,
  finalize,
  from,
  interval,
  map,
  Observable,
  pipe,
  Subscription,
  switchMap,
  take,
  tap,
  UnaryFunction,
} from 'rxjs';
import { checkIfRefreshIsAlreadyRunning } from '../../rxjs/check-if-refresh-is-already-running.function';
import { checkIfRefreshIsRequired } from '../../rxjs/check-if-refresh-is-required';
import { LoggerWithConfigurationService } from '../internal/logger-with-configuration.service';

/**
 * This service is responsible for managing the token refresh process,
 * including periodic checks, locking mechanism, and token validation.
 */
export class IntervalService {
  public static readonly oauthRefreshStorageKey = `oauth/refresh-process-running`;

  public silentRenewSubscription: Subscription | null = null;

  private readonly oidcSecurityService = inject(OidcSecurityService);

  private readonly storage = inject(StorageService);

  private readonly publicEventsService = inject(PublicEventsService);

  private readonly loggerService = inject(LoggerWithConfigurationService);

  private readonly injector = inject(Injector);

  protected readonly waitFor: UnaryFunction<Observable<unknown>, Observable<unknown>> = pipe();

  private get authConfig(): OpenIdConfiguration | undefined {
    const configurations = this.oidcSecurityService.getConfigurations();

    return configurations.length ? configurations[0] : undefined;
  }

  /**
   * Starts the periodic token check process.
   *
   * This method waits for the ConfigLoaded event from angular-auth-oidc-client
   * before starting the actual token check to ensure configuration is fully loaded.
   */
  public startPeriodicTokenCheck(): void {
    this.loggerService.logDebug(
      this.prefixMessage(`Waiting for configuration to be loaded before starting token check.`),
    );

    this.silentRenewSubscription = this.publicEventsService
      .registerForEvents()
      .pipe(
        filter((notification) => notification.type === EventTypes.ConfigLoaded),
        take(1),
        tap(() => this.publicEventsService.fireEvent(EventTypes.SilentRenewStarted)),
        tap(() => void this.resetRefreshState()),
        switchMap(() => {
          const refreshInterval = this.authConfig?.refreshTokenRetryInSeconds ?? 5;
          this.loggerService.logDebug(
            this.prefixMessage(
              `Configuration loaded. Setup token refresh interval every ${refreshInterval} seconds.`,
            ),
          );
          return interval(refreshInterval * 1000);
        }),
        this.waitFor,
        checkIfRefreshIsRequired(this.injector),
        filter((needsRefresh) => needsRefresh),
        checkIfRefreshIsAlreadyRunning(this.injector),
        filter((isRefreshAlreadyRunning) => !isRefreshAlreadyRunning),
        exhaustMap(() => this.lock()),
        exhaustMap(() => this.forceRefresh()),
        map(() => void this.unlock()),
        finalize(() => void this.unlock(true)),
      )
      .subscribe();
  }

  /**
   * Remove the storage value at startup to handle cases where the process was interrupted.
   */
  private async resetRefreshState(): Promise<void> {
    return this.storage
      .remove(IntervalService.oauthRefreshStorageKey)
      .then(() => {
        this.loggerService.logDebug(
          this.prefixMessage(`Reset refresh state, so we start with a clean state.`),
        );
      })
      .catch((error: unknown) => {
        this.loggerService.logError(
          this.prefixMessage('Error removing refresh process status from storage at startup:'),
          error,
        );
      });
  }

  private forceRefresh() {
    const configId = this.authConfig?.configId;

    return this.oidcSecurityService.forceRefreshSession({}, configId).pipe(
      map((loginResponse) => {
        this.loggerService.logDebug(
          this.prefixMessage(
            `Token refresh completed. isAuthenticated: ${loginResponse.isAuthenticated}`,
          ),
        );

        if (loginResponse.isAuthenticated) {
          this.loggerService.logDebug(
            this.prefixMessage(`Token refresh successful and saved. Access token is now valid.`),
          );

          // Add a delay to ensure the token is properly saved before proceeding.
          return from(new Promise<void>((resolve) => setTimeout(() => resolve(), 1000))).pipe(
            map(() => {
              this.loggerService.logDebug(
                this.prefixMessage(`Waited for token to be properly saved.`),
              );
              return loginResponse;
            }),
          );
        }
        this.loggerService.logError(
          this.prefixMessage(
            `Token refresh failed. isAuthenticated: ${loginResponse.isAuthenticated}, error: ${loginResponse.errorMessage}`,
          ),
        );
        return from(Promise.resolve(loginResponse));
      }),
    );
  }

  private lock(): Observable<string> {
    const lockId = window.crypto.randomUUID();

    this.loggerService.logDebug(
      this.prefixMessage(`Lock the process. I will refresh the token. [lockID: ${lockId}]`),
    );

    return from(this.storage.set(IntervalService.oauthRefreshStorageKey, lockId)).pipe(
      // Verify the lock was successfully set by reading it back.
      switchMap(() => from(this.storage.get(IntervalService.oauthRefreshStorageKey))),
      map((value) => {
        if (value !== lockId) {
          this.loggerService.logWarning(
            this.prefixMessage(
              `Failed to lock the process. [lockID: ${lockId}, activeLockID: ${value}]`,
            ),
          );
          return value as string;
        }

        this.loggerService.logDebug(this.prefixMessage(`Lock confirmed! [activeLockID: ${value}]`));
        return value as string;
      }),
    );
  }

  private async unlock(finalized: boolean = false): Promise<void> {
    if (finalized) {
      this.loggerService.logDebug(this.prefixMessage(`Unlock the process. Process finalized.`));
    } else {
      this.loggerService.logDebug(
        this.prefixMessage(`Unlock the process. I have refreshed the token.`),
      );
    }

    return this.storage.remove(IntervalService.oauthRefreshStorageKey).catch((error: unknown) => {
      this.loggerService.logError(
        this.prefixMessage('Error removing refresh process status from storage:'),
        error,
      );
    });
  }

  private prefixMessage(message: string): string {
    const timestamp = format(new Date(), 'HH:mm:ss');

    return `\x1b[31m[withSilentRenewLocking ${timestamp}] ${message}\x1b[0m`;
  }
}
